import { StatusCode } from "services/api/flagbags/types";
import { normalizeFlagbagServerStatus } from "./flagbagStatusUtils";

export interface VolunteerSpareAssignmentDb {
	getAll: <T = any>() => Promise<T[] | null>;
}

/** Assigned to a volunteer but not yet collected — blocks spare assignment. */
const UNCOLLECTED_ASSIGNED_FLAGBAG_STATUSES = new Set<StatusCode>([
	StatusCode.ASSIGNED,
	StatusCode.DISTRIBUTED,
	StatusCode.DISTRIBUTED_OTHER,
	StatusCode.LATE,
]);

function convertApiStatusToStatusCode(apiStatus: string): StatusCode {
	return normalizeFlagbagServerStatus(apiStatus);
}

function normalizeFlagbagStatusKey(status: unknown): StatusCode | "" {
	if (status === null || status === undefined || status === "") return "";
	return convertApiStatusToStatusCode(String(status));
}

function isUncollectedAssignedFlagbagStatus(status: unknown): boolean {
	const key = normalizeFlagbagStatusKey(status);
	if (!key) return false;
	return UNCOLLECTED_ASSIGNED_FLAGBAG_STATUSES.has(key);
}

function isMeaningfulVolunteerId(volunteerId: unknown): volunteerId is string {
	return typeof volunteerId === "string" && volunteerId !== "" && volunteerId !== "0";
}

function belongsToStation(record: { stationId?: string; stationID?: string }, stationId?: string): boolean {
	if (!stationId) return true;
	return record.stationId === stationId || record.stationID === stationId;
}

export async function getVolunteersWithActiveFlagbagOffline(
	offlineDB: VolunteerSpareAssignmentDb,
	flagBagDB: VolunteerSpareAssignmentDb,
	excludeFlagBagId?: string,
	stationId?: string,
): Promise<Map<string, { flagBagId: string; flagbagNum: string }>> {
	const volunteersWithActiveFlagbag = new Map<string, { flagBagId: string; flagbagNum: string }>();

	const recordBlockingVolunteer = (
		bagId: string,
		flagbagNum: string,
		effectiveVolunteerId: unknown,
		effectiveStatus: unknown,
	) => {
		if (excludeFlagBagId && bagId === excludeFlagBagId) return;
		if (!isMeaningfulVolunteerId(effectiveVolunteerId)) return;
		if (!isUncollectedAssignedFlagbagStatus(effectiveStatus)) return;
		const volunteerKey = String(effectiveVolunteerId);
		if (!volunteersWithActiveFlagbag.has(volunteerKey)) {
			volunteersWithActiveFlagbag.set(volunteerKey, { flagBagId: bagId, flagbagNum });
		}
	};

	const offlineActions = (await offlineDB.getAll().catch(() => null)) || [];
	const latestOfflineActionByBagId = new Map<string, any>();
	offlineActions.forEach((action: any) => {
		if (!action?.flagBagID) return;
		if (!belongsToStation(action, stationId)) return;
		const prev = latestOfflineActionByBagId.get(action.flagBagID);
		if (String(action.updatedAt || "0") > String(prev?.updatedAt || "0")) {
			latestOfflineActionByBagId.set(action.flagBagID, action);
		}
	});

	const allFlagbags = (await flagBagDB.getAll().catch(() => null)) || [];
	const checkedBagIds = new Set<string>();

	for (const fb of allFlagbags) {
		const bagId = fb?.id;
		if (!bagId) continue;
		if (!belongsToStation(fb, stationId)) continue;
		checkedBagIds.add(bagId);

		const latestAction = latestOfflineActionByBagId.get(bagId);
		const effectiveVolunteerId = latestAction?.volunteerId ?? fb.volunteerId;
		const effectiveStatus =
			latestAction?.status !== undefined && latestAction?.status !== null
				? latestAction.status
				: fb.status || fb.currentStatus;

		recordBlockingVolunteer(
			bagId,
			fb.flagbagNum || fb.flagBagNum || bagId,
			effectiveVolunteerId,
			effectiveStatus,
		);
	}

	latestOfflineActionByBagId.forEach((action, bagId) => {
		if (checkedBagIds.has(bagId)) return;
		if (!belongsToStation(action, stationId)) return;
		recordBlockingVolunteer(
			bagId,
			action.flagbagNum || bagId,
			action.volunteerId,
			action.status,
		);
	});

	return volunteersWithActiveFlagbag;
}

export async function getVolunteerIdsWithActiveFlagbagOffline(
	offlineDB: VolunteerSpareAssignmentDb,
	flagBagDB: VolunteerSpareAssignmentDb,
	excludeFlagBagId?: string,
	stationId?: string,
): Promise<Set<string>> {
	const volunteersWithActiveFlagbag = await getVolunteersWithActiveFlagbagOffline(
		offlineDB,
		flagBagDB,
		excludeFlagBagId,
		stationId,
	);
	return new Set(volunteersWithActiveFlagbag.keys());
}

export async function findVolunteerActiveFlagbagOffline(
	offlineDB: VolunteerSpareAssignmentDb,
	flagBagDB: VolunteerSpareAssignmentDb,
	volunteerId: string,
	excludeFlagBagId?: string,
): Promise<{ flagBagId: string; flagbagNum: string } | null> {
	if (!isMeaningfulVolunteerId(volunteerId)) return null;
	const volunteersWithActiveFlagbag = await getVolunteersWithActiveFlagbagOffline(
		offlineDB,
		flagBagDB,
		excludeFlagBagId,
	);
	return volunteersWithActiveFlagbag.get(String(volunteerId)) ?? null;
}

export async function assertVolunteerCanReceiveSpareFlagbag(
	offlineDB: VolunteerSpareAssignmentDb,
	flagBagDB: VolunteerSpareAssignmentDb,
	volunteerId: string,
	excludeFlagBagId: string,
): Promise<void> {
	const existing = await findVolunteerActiveFlagbagOffline(
		offlineDB,
		flagBagDB,
		volunteerId,
		excludeFlagBagId,
	);
	if (existing) {
		throw {
			errorCode: -1,
			errorMsg: `義工已分配旗袋，無法再分配後備旗袋\n旗袋編號：${existing.flagbagNum}`,
		};
	}
}
